import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

export default function AuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Get all the params from the URL
        const access_token = searchParams.get('access_token');
        const refresh_token = searchParams.get('refresh_token');
        const type = searchParams.get('type');
        
        // Check for dev mode magic link format
        const devToken = searchParams.get('token');
        const email = searchParams.get('email');
        
        if (!access_token && !devToken) {
          // Check if we have error params
          const error = searchParams.get('error');
          const errorDescription = searchParams.get('error_description');
          
          if (error) {
            setError(errorDescription || error);
            setLoading(false);
            return;
          }
          
          setError('No authentication token found');
          setLoading(false);
          return;
        }
        
        // Handle dev mode magic link
        if (devToken && email) {
          console.log('🔧 Dev mode magic link callback:', { devToken, email });
          // In dev mode, redirect directly to intake form since user is already authenticated
          const redirectTo = '/intake-form';
          window.location.href = redirectTo;
          return;
        }

        // Standard Supabase Auth callback flow
        const response = await fetch('/api/auth/callback', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            access_token,
            refresh_token,
            type
          }),
        });

        if (response.ok) {
          // Redirect to the intended destination
          const redirectTo = searchParams.get('redirect_to') || '/portal/dashboard';
          // Use window.location for full page reload to ensure session is properly set
          window.location.href = redirectTo;
        } else {
          const data = await response.json();
          setError(data.error || 'Authentication failed');
          setLoading(false);
        }
      } catch (err: any) {
        console.error('Auth callback error:', err);
        setError('An error occurred during authentication');
        setLoading(false);
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Completing sign in...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
              <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="mt-4 text-lg font-medium text-gray-900">Authentication Error</h2>
            <p className="mt-2 text-sm text-gray-600">{error}</p>
            <div className="mt-6">
              <button
                onClick={() => navigate('/portal/login')}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
              >
                Back to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}